import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react'

const MIN_DATE = new Date(1975, 0, 1)
const MAX_DATE = new Date(2030, 0, 1)

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]
const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Pzr']

const fmt = (d) => {
  if (!d) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getFullYear()}`
}

/** Parse dd.mm.yyyy string to Date, returns null if invalid */
const parseDateStr = (str) => {
  if (!str) return null
  // Accept dd.mm.yyyy or dd/mm/yyyy or dd-mm-yyyy
  const m = str.match(/^(\d{1,2})[./\-](\d{1,2})[./\-](\d{4})$/)
  if (!m) return null
  const day = parseInt(m[1], 10)
  const month = parseInt(m[2], 10) - 1
  const year = parseInt(m[3], 10)
  if (month < 0 || month > 11 || day < 1 || day > 31 || year < 1975 || year > 2030) return null
  const d = new Date(year, month, day)
  // Validate the date is real (e.g. 31.02.2026 → invalid)
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null
  return d
}

const isSame = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const inRange = (d, s, e) => {
  if (!s || !e) return false
  const t = d.getTime()
  return t >= s.getTime() && t <= e.getTime()
}

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1)
  let startDay = first.getDay() - 1
  if (startDay < 0) startDay = 6
  const days = []
  for (let i = startDay - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month, -i), outside: true })
  }
  const last = new Date(year, month + 1, 0).getDate()
  for (let i = 1; i <= last; i++) {
    days.push({ date: new Date(year, month, i), outside: false })
  }
  const target = days.length <= 35 ? 35 : 42
  while (days.length < target) {
    days.push({ date: new Date(year, month + 1, days.length - last - startDay + 1), outside: true })
  }
  return days
}

/** Editable date input — shows dd.mm.yyyy, accepts typing */
function DateInput({ value, onDateChange, placeholder, isActive, onFocus, compact = false }) {
  const [text, setText] = useState(fmt(value))
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef(null)

  // Sync external value changes
  useEffect(() => {
    setText(fmt(value))
    setInvalid(false)
  }, [value])

  const handleChange = (e) => {
    let raw = e.target.value
    setText(raw)
    setInvalid(false)

    // Auto-insert dots: 01 → 01. , 01.02 → 01.02.
    const digits = raw.replace(/[^0-9]/g, '')
    if (digits.length >= 8) {
      const formatted = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`
      setText(formatted)
      const parsed = parseDateStr(formatted)
      if (parsed) {
        setInvalid(false)
        onDateChange(parsed)
      } else {
        setInvalid(true)
      }
      return
    }

    // Try parsing what user typed
    const parsed = parseDateStr(raw)
    if (parsed) {
      setInvalid(false)
      onDateChange(parsed)
    }
  }

  const handleBlur = () => {
    // On blur, try to parse and fix format
    const parsed = parseDateStr(text)
    if (parsed) {
      setText(fmt(parsed))
      setInvalid(false)
      onDateChange(parsed)
    } else if (text && text.trim()) {
      setInvalid(true)
    } else {
      // Empty — revert to current value
      setText(fmt(value))
      setInvalid(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleBlur()
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      placeholder={placeholder}
      className={[
        compact ? 'text-xs w-[74px] bg-transparent outline-none text-center' : 'text-sm w-[90px] bg-transparent outline-none text-center',
        invalid ? 'text-red-500' : value ? 'text-gray-800 font-medium' : 'text-gray-400',
        isActive ? 'text-primary-600 underline' : '',
      ].filter(Boolean).join(' ')}
    />
  )
}

export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  className = '',
  single = false,
  compact = false,   // dar yerler (tablo hunisi menüsü) için küçük giriş alanı
}) {
  const [open, setOpen] = useState(false)
  const [activeField, setActiveField] = useState('start')
  const [tempStart, setTempStart] = useState(startDate || null)
  const [tempEnd, setTempEnd] = useState(endDate || null)
  const [viewYear, setViewYear] = useState(new Date().getFullYear())
  const [viewMonth, setViewMonth] = useState(new Date().getMonth())
  const [popupStyle, setPopupStyle] = useState({})
  const ref = useRef(null)
  const inputRef = useRef(null)
  const popupRef = useRef(null)
  // ⚠️ Takvim body'ye portal'la çizilir: üst öğelerden birinde backdrop-filter /
  // transform varsa `position: fixed` o öğeye hapsolur ve takvim kırpılır (Satış
  // Raporları yapışkan başlığı — backdrop-blur). Tablo hunisi (compact) menünün
  // DOM çocuğu kalmalı: menünün dışarı-tıkla kapanması tetiklenmesin.
  const usePortal = !compact

  useEffect(() => {
    if (!open) {
      setTempStart(startDate || null)
      setTempEnd(endDate || null)
    }
  }, [startDate, endDate, open])

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)
          && !(popupRef.current && popupRef.current.contains(e.target))) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const checkPosition = useCallback(() => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    const popupH = 400
    const isMobile = window.innerWidth < 480
    const popupW = isMobile ? 304 : 410
    const spaceBelow = window.innerHeight - rect.bottom
    const goUp = spaceBelow < popupH && rect.top > popupH

    let left = isMobile ? Math.max(4, (window.innerWidth - popupW) / 2) : rect.left
    if (!isMobile && left + popupW > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - popupW - 8)
    }

    setPopupStyle({
      position: 'fixed',
      left,
      ...(goUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 9999,
    })
  }, [])

  const openPopup = (field) => {
    setActiveField(field)
    const d = field === 'start' ? (tempStart || new Date()) : (tempEnd || new Date())
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())
    checkPosition()
    setOpen(true)
  }

  // Direct date input from text fields — apply immediately without popup
  const handleDirectDateChange = (field, date) => {
    if (single) {
      onChange({ start: date, end: date })
      return
    }
    if (field === 'start') {
      const newEnd = endDate && date > endDate ? date : endDate
      onChange({ start: date, end: newEnd })
    } else {
      const newStart = startDate && date < startDate ? date : startDate
      onChange({ start: newStart, end: date })
    }
  }

  const handleDayClick = (date) => {
    if (single) {
      setTempStart(date)
      setTempEnd(date)
      onChange({ start: date, end: date })
      setOpen(false)
      return
    }
    if (activeField === 'start') {
      setTempStart(date)
      if (!tempEnd || date > tempEnd) setActiveField('end')
    } else {
      if (tempStart && date < tempStart) {
        setTempEnd(tempStart)
        setTempStart(date)
      } else {
        setTempEnd(date)
      }
    }
  }

  const handlePreset = (date) => {
    // Tekli modda hazır seçim (Bugün, Ay Sonu…) tek tıkla uygulanır — "Uygula" beklemez
    if (single) {
      setTempStart(date)
      setTempEnd(date)
      onChange({ start: date, end: date })
      setOpen(false)
      return
    }
    if (activeField === 'start') {
      setTempStart(date)
      setActiveField('end')
    } else {
      setTempEnd(date)
    }
    setViewYear(date.getFullYear())
    setViewMonth(date.getMonth())
  }

  const handleApply = () => {
    if (tempStart && tempEnd) {
      const s = tempStart <= tempEnd ? tempStart : tempEnd
      const e = tempStart <= tempEnd ? tempEnd : tempStart
      onChange({ start: s, end: e })
    }
    setOpen(false)
  }

  const handleReset = () => {
    setTempStart(null)
    setTempEnd(null)
    onChange({ start: null, end: null })
    setOpen(false)
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) }
    else setViewMonth(viewMonth - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) }
    else setViewMonth(viewMonth + 1)
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const presets = [
    { label: 'Maksimum', date: MAX_DATE },
    { label: 'Yıl Sonu', date: new Date(now.getFullYear(), 11, 31) },
    { label: 'Ay Sonu', date: new Date(now.getFullYear(), now.getMonth() + 1, 0) },
    { label: 'Bugün', date: today, highlight: true },
    { label: 'Ay Başı', date: new Date(now.getFullYear(), now.getMonth(), 1) },
    { label: 'Yıl Başı', date: new Date(now.getFullYear(), 0, 1) },
    { label: 'Minimum', date: MIN_DATE },
  ]

  const days = getCalendarDays(viewYear, viewMonth)
  const hasSelection = startDate || endDate

  return (
    <div className={`relative ${className}`} ref={ref}>
      {/* Input area */}
      <div
        ref={inputRef}
        className={compact
          ? 'flex items-center gap-1 px-2 py-1.5 border border-gray-200 rounded-lg bg-gray-50'
          : 'flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50'}
      >
        <button
          type="button"
          onClick={() => openPopup('start')}
          className="text-gray-400 hover:text-primary-500 shrink-0 transition-colors"
          title="Takvim aç"
        >
          <Calendar size={16} />
        </button>
        <DateInput
          value={startDate}
          onDateChange={(d) => handleDirectDateChange('start', d)}
          placeholder="gg.aa.yyyy"
          isActive={open && activeField === 'start'}
          onFocus={() => { if (!open) openPopup('start') }}
          compact={compact}
        />
        {!single && (
          <>
            <span className="text-gray-300">—</span>
            <DateInput
              value={endDate}
              onDateChange={(d) => handleDirectDateChange('end', d)}
              placeholder="gg.aa.yyyy"
              isActive={open && activeField === 'end'}
              onFocus={() => { if (!open) openPopup('end') }}
              compact={compact}
            />
          </>
        )}
        {hasSelection && (
          <button type="button" onClick={handleReset} className="ml-1 p-0.5 hover:bg-gray-200 rounded">
            <X size={14} className="text-gray-400" />
          </button>
        )}
      </div>

      {/* Calendar popup */}
      {open && (usePortal ? (p) => createPortal(p, document.body) : (p) => p)(
        <div
          ref={popupRef}
          className="bg-white rounded-xl shadow-xl border border-gray-200 flex overflow-hidden"
          style={popupStyle}
        >
          {/* Calendar side */}
          <div className="p-4" style={{ width: 296 }}>
            {/* Active field indicator */}
            {!single && (
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setActiveField('start')}
                  className={`flex-1 text-center px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeField === 'start' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  Başlangıç: {fmt(tempStart) || '—'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveField('end')}
                  className={`flex-1 text-center px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeField === 'end' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  Bitiş: {fmt(tempEnd) || '—'}
                </button>
              </div>
            )}

            {/* Month/Year nav */}
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-lg">
                <ChevronLeft size={16} className="text-gray-500" />
              </button>
              <span className="text-sm font-bold text-gray-700">
                {MONTHS[viewMonth]} {viewYear}
              </span>
              <button type="button" onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-lg">
                <ChevronRight size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7">
              {DAYS.map((d) => (
                <div key={d} className="h-7 flex items-center justify-center text-[11px] font-semibold text-gray-400">
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7">
              {days.map(({ date, outside }, i) => {
                const isToday = isSame(date, today)
                const isStart = isSame(date, tempStart)
                const isEnd = isSame(date, tempEnd)
                const sorted = tempStart && tempEnd
                  ? (tempStart <= tempEnd ? [tempStart, tempEnd] : [tempEnd, tempStart])
                  : [null, null]
                const isIn = sorted[0] && sorted[1] && inRange(date, sorted[0], sorted[1])
                const isSelected = isStart || isEnd

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleDayClick(date)}
                    className={[
                      'h-8 flex items-center justify-center text-xs rounded-md cursor-pointer transition-all',
                      outside && 'text-gray-300',
                      !outside && isSelected && 'bg-primary-500 text-white font-bold',
                      !outside && !isSelected && isIn && 'bg-primary-50 text-primary-700 font-medium',
                      !outside && !isSelected && !isIn && 'text-gray-700 hover:bg-gray-100',
                      isToday && !isSelected && 'ring-2 ring-primary-400 ring-inset',
                    ].filter(Boolean).join(' ')}
                  >
                    {date.getDate()}
                  </button>
                )
              })}
            </div>

            {/* Apply / Cancel */}
            <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Kapat
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!tempStart || !tempEnd}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Uygula
              </button>
            </div>
          </div>

          {/* Presets side — hidden on very small screens */}
          <div className="border-l border-gray-100 bg-gray-50 py-3 px-1.5 hidden sm:flex flex-col gap-0.5" style={{ width: 110 }}>
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => handlePreset(p.date)}
                className={[
                  'px-2.5 py-2 text-xs font-semibold rounded-lg text-left transition-colors',
                  p.highlight
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'text-gray-600 hover:bg-gray-200',
                ].join(' ')}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
